import { useEffect, useState } from "react";
import api from "../lib/api";
import { PhoneCall, CheckCircle2, XCircle, Clock, TrendingUp, Signal, IndianRupee, Users, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "#71717A",
  not_connected: "#A1A1AA",
  completed: "#16A34A",
  hold: "#D97706",
  rejected: "#DC2626",
  follow_up: "#2563EB",
};

const STATUS_LABEL = {
  new: "New", not_connected: "Not Connected", completed: "Completed",
  hold: "Hold", rejected: "Rejected", follow_up: "Follow-up",
};

function Metric({ label, value, sub, icon: Icon, accent }) {
  return (
    <div data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-sm border border-zinc-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${accent || "text-zinc-400"}`} />}
      </div>
      <div className="mt-3 font-heading text-3xl font-bold tracking-tight text-black">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/reports/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [user]);

  const toggleAutoSignal = async () => {
    const next = !settings?.auto_signal;
    try {
      const { data: s } = await api.patch("/settings", { auto_signal: next });
      setSettings(s);
      toast.success(next ? "Auto-signal enabled — completed leads will fire to Meta automatically" : "Auto-signal disabled");
    } catch (e) {
      toast.error("Could not update setting");
    }
  };

  if (!data) return <div className="p-10 text-sm text-zinc-500">Loading dashboard…</div>;

  const statusRows = Object.keys(STATUS_LABEL).map((k) => ({
    key: k, name: STATUS_LABEL[k], value: data.status_counts[k] || 0, color: STATUS_COLORS[k],
  }));

  return (
    <div className="p-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-widest text-zinc-500">Overview</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-black">Control Room</h1>
        </div>
        <button
          data-testid="auto-signal-toggle"
          onClick={toggleAutoSignal}
          className={`group inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-xs font-medium transition-colors ${
            settings?.auto_signal
              ? "border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
          }`}
        >
          <Zap className={`h-3.5 w-3.5 ${settings?.auto_signal ? "fill-emerald-500 text-emerald-500" : ""}`} />
          Auto-signal to Meta
          <span className={`relative flex h-4 w-7 items-center rounded-full transition-colors ${settings?.auto_signal ? "bg-emerald-500" : "bg-zinc-300"}`}>
            <span className={`absolute h-3 w-3 rounded-full bg-white transition-transform ${settings?.auto_signal ? "translate-x-3.5" : "translate-x-0.5"}`} />
          </span>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric label="Total Leads" value={data.total_leads} sub={`${data.assigned} assigned · ${data.unassigned} open`} icon={Users} accent="text-zinc-700" />
        <Metric label="Revenue" value={`₹${data.total_sales.toLocaleString("en-IN")}`} sub={`${data.completed_count} closed deals`} icon={IndianRupee} accent="text-emerald-600" />
        <Metric label="Conversion" value={`${data.conversion_rate}%`} sub="completed / total leads" icon={TrendingUp} accent="text-blue-600" />
        <Metric label="Quality Signals" value={data.quality_signals} sub="sent to Meta" icon={Signal} accent="text-emerald-600" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-sm border border-zinc-200 bg-white p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">Status breakdown</div>
              <div className="font-heading text-lg font-bold text-black">Where every lead stands</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusRows} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#52525B" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#52525B" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E4E4E7" }} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {statusRows.map((row) => <Cell key={row.key} fill={row.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-sm border border-zinc-200 bg-white p-5">
          <div className="mb-2 text-[11px] uppercase tracking-widest text-zinc-500">Distribution</div>
          <div className="mb-3 font-heading text-lg font-bold text-black">Pipeline share</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={statusRows.filter(r => r.value > 0)} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                {statusRows.map((row) => <Cell key={row.key} fill={row.color} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E4E4E7" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1.5">
            {statusRows.filter(r => r.value > 0).map(r => (
              <div key={r.key} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-zinc-700"><span className="h-2 w-2 rounded-full" style={{background: r.color}}/> {r.name}</span>
                <span className="font-semibold text-black">{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-sm border border-zinc-200 bg-white">
        <div className="border-b border-zinc-200 px-5 py-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Team performance</div>
          <div className="font-heading text-lg font-bold text-black">Telecaller leaderboard</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="telecaller-report-table">
            <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-5 py-3">Telecaller</th>
                <th className="px-3 py-3">Assigned</th>
                <th className="px-3 py-3">Completed</th>
                <th className="px-3 py-3">Hold</th>
                <th className="px-3 py-3">Follow-up</th>
                <th className="px-3 py-3">Rejected</th>
                <th className="px-3 py-3">Not connected</th>
                <th className="px-3 py-3">Sales (₹)</th>
                <th className="px-5 py-3">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {data.per_telecaller.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-sm text-zinc-500">No assignments yet — assign leads from the Leads page.</td></tr>
              )}
              {data.per_telecaller.map((r) => (
                <tr key={r.telecaller_id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="px-5 py-3 font-medium text-black">{r.name}</td>
                  <td className="px-3 py-3 tabular-nums">{r.total}</td>
                  <td className="px-3 py-3 tabular-nums text-emerald-700">{r.completed}</td>
                  <td className="px-3 py-3 tabular-nums text-amber-700">{r.hold}</td>
                  <td className="px-3 py-3 tabular-nums text-blue-700">{r.follow_up}</td>
                  <td className="px-3 py-3 tabular-nums text-red-700">{r.rejected}</td>
                  <td className="px-3 py-3 tabular-nums text-zinc-700">{r.not_connected}</td>
                  <td className="px-3 py-3 tabular-nums font-semibold">₹{r.sales.toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3 tabular-nums">{r.conversion}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
